import React, { useCallback } from 'react';
import classNames from 'classnames';

import {
  getSecondaryLabel,
  buildRangeKey,
  formatRangeLabel,
  isNonAidRange,
} from '../definition-tab.constants';

const RANK_COLUMN_WIDTH_PX = 48;
const RANGE_COLUMN_WIDTH_PX = 160;

const formatDisplayValue = (value: unknown): string => {
  if (value == null || value === '') return '';

  const num =
    typeof value === 'number' ? value : Number.parseFloat(value as string);

  return Number.isFinite(num) ? num.toLocaleString('en-US') : '0';
};

const parseNumericInput = (raw: string): number => {
  const cleaned = raw.replace(/[^0-9.-]/g, '');
  const num = Number.parseFloat(cleaned);

  return Number.isFinite(num) ? num : 0;
};

interface MatrixCellProps {
  value: number;
  prefix?: string;
  suffix?: string;
  onChange: (val: number) => void;
  readOnly?: boolean;
  'data-testid'?: string;
}

interface CalculationRange {
  from: number | null;
  to: number | null;
}

interface CalculationCellValue {
  amount: number;
  discount: number;
}

interface AwardCalculationTabContentProps {
  ranges: CalculationRange[];
  calculatedBy: string;
  rankIds: Array<string | number>;
  rankIdToDisplayNumber: Map<string | number, string | number>;
  getCell: (
    rankId: string | number,
    rangeKey: string
  ) => CalculationCellValue;
  updateCell: (
    rankId: string | number,
    rangeKey: string,
    field: 'amount' | 'discount',
    value: number
  ) => void;
}

const MatrixCell = ({
  value,
  prefix,
  suffix,
  onChange,
  readOnly,
  'data-testid': dataTestId,
}: MatrixCellProps): React.JSX.Element => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(parseNumericInput(e.target.value));
    },
    [onChange]
  );

  if (readOnly) {
    return (
      <div className="flex flex-1 items-center justify-center gap-1 h-10 p-2 text-sm leading-5 text-gray-700 whitespace-nowrap">
        -
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center gap-1 h-10 p-2 whitespace-nowrap">
      {prefix && (
        <span className="text-sm leading-5 text-gray-500 shrink-0">
          {prefix}
        </span>
      )}
      <input
        type="text"
        inputMode="numeric"
        value={formatDisplayValue(value)}
        onChange={handleChange}
        className="w-full min-w-0 text-sm leading-5 text-gray-700 text-center bg-transparent outline-none border-none p-0"
        data-testid={dataTestId}
      />
      {suffix && (
        <span className="text-sm leading-5 text-gray-500 shrink-0">
          {suffix}
        </span>
      )}
    </div>
  );
};

const AwardCalculationTabContent = ({
  ranges,
  calculatedBy,
  rankIds,
  rankIdToDisplayNumber,
  getCell,
  updateCell,
}: AwardCalculationTabContentProps): React.JSX.Element => {
  const secondaryLabel = getSecondaryLabel(calculatedBy);

  if (!ranges.length) {
    return (
      <div
        className="flex items-center justify-center p-6 text-sm text-gray-500"
        data-testid="award-calculation-tab-content"
      >
        Define ranges in the Definition tab to configure award calculations.
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 p-3 min-w-0 max-w-full"
      data-testid="award-calculation-tab-content"
    >
      <span className="text-base font-bold text-gray-700 leading-6">
        Funds using {secondaryLabel === '%' ? 'Need %' : 'Flat $'}
      </span>

      <div className="w-full max-w-full overflow-x-auto scrollbar-thin">
        <div
          className="border border-navy-100 rounded-md overflow-hidden"
          style={{
            minWidth: `${
              RANK_COLUMN_WIDTH_PX + ranges.length * RANGE_COLUMN_WIDTH_PX
            }px`,
          }}
        >
          <div className="flex">
            <div className="shrink-0 w-12 flex items-center justify-center px-2 border-b border-navy-100">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-[1.6px] leading-4">
                Rank
              </span>
            </div>

            <div className="flex flex-1 min-w-0 flex-col border-l border-navy-100">
              <div className="flex">
                {ranges.map((range, index) => (
                  <div
                    key={buildRangeKey(range)}
                    className={classNames(
                      'flex-1 min-w-0 flex items-center justify-center pt-2 pb-1.5 px-2',
                      { 'border-l border-navy-100': index > 0 }
                    )}
                  >
                    <span
                      className={classNames(
                        'text-xs font-semibold uppercase tracking-[1.6px] leading-4 text-center truncate',
                        isNonAidRange(range) ? 'text-gray-700' : 'text-navy-800'
                      )}
                    >
                      {formatRangeLabel(range)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex border-b border-navy-100">
                {ranges.map((range, index) => (
                  <div
                    key={buildRangeKey(range)}
                    className={classNames(
                      'flex flex-1 min-w-0 items-center justify-between py-0.5 text-xs font-semibold text-gray-500 uppercase leading-4',
                      { 'border-l border-navy-100': index > 0 }
                    )}
                  >
                    <span className="flex-1 text-center">$</span>
                    <span className="flex-1 text-center">{secondaryLabel}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {rankIds.map((rankId, rankIndex) => {
            const isLastRow = rankIndex === rankIds.length - 1;
            const displayRank = rankIdToDisplayNumber.get(rankId) ?? rankId;

            return (
              <div
                key={rankId}
                className="flex"
                data-testid={`matrix-row-${rankId}`}
              >
                <div
                  className={classNames(
                    'shrink-0 w-12 flex items-center justify-center py-2',
                    { 'border-b border-navy-100': !isLastRow }
                  )}
                >
                  <span className="text-xs font-semibold text-gray-700 leading-4">
                    {displayRank}
                  </span>
                </div>

                <div
                  className={classNames(
                    'flex flex-1 min-w-0 border-l border-navy-100',
                    { 'border-b border-navy-100': !isLastRow }
                  )}
                >
                  {ranges.map((range, colIndex) => {
                    const rangeKey = buildRangeKey(range);
                    const cell = getCell(rankId, rangeKey);
                    const nonAid = isNonAidRange(range);

                    return (
                      <div
                        key={rangeKey}
                        className={classNames(
                          'flex flex-1 min-w-0 items-center',
                          { 'border-l border-navy-100': colIndex > 0 }
                        )}
                        data-testid={`matrix-cell-${rankId}-${rangeKey}`}
                      >
                        <MatrixCell
                          value={cell.amount}
                          prefix="$"
                          onChange={(val) =>
                            updateCell(rankId, rangeKey, 'amount', val)
                          }
                          data-testid={`matrix-input-amount-${rankId}-${rangeKey}`}
                        />
                        <MatrixCell
                          value={cell.discount}
                          suffix={secondaryLabel}
                          readOnly={nonAid}
                          onChange={(val) =>
                            updateCell(rankId, rangeKey, 'discount', val)
                          }
                          data-testid={`matrix-input-discount-${rankId}-${rangeKey}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AwardCalculationTabContent;
